<template>
  <div class="coupon-center-container" @click="closeAllDropdowns">
    <h1 class="page-title">Coupon Center</h1>

    <div class="filter-section">
      <div class="filter-row filter-row-primary">
        <div
          class="filter-box filter-box--search field-search-item"
          :class="{ 'is-open': openDd === 'searchField' }"
        >
          <span
            role="button"
            tabindex="0"
            class="field-type-btn"
            @click.stop="toggleDropdown('searchField')"
            @keydown.enter.prevent="toggleDropdown('searchField')"
            @keydown.space.prevent="toggleDropdown('searchField')"
          >
            <span class="field-type-label">{{ currentSearchFieldLabel }}</span>
            <img :src="downarrowIcon" alt="" class="select-arrow-icon downarrow-inline" />
          </span>
          <span class="field-split" aria-hidden="true" />
          <input
            v-model="filters.searchQuery"
            type="text"
            class="filter-input-inline"
            placeholder="Search.."
            maxlength="50"
            @keyup.enter="handleSearch"
            @focus="openDd = null"
          />
          <div v-if="openDd === 'searchField'" class="dropdown-panel search-field-panel" @click.stop>
            <div
              v-for="option in searchFieldOptions"
              :key="option.value"
              class="dropdown-option"
              :class="{ active: filters.searchField === option.value }"
              @click="selectSearchField(option.value)"
            >
              <span>{{ option.label }}</span>
            </div>
          </div>
        </div>

        <div
          class="filter-box filter-box--select"
          :class="{ 'is-open': openDd === 'type' }"
          @click.stop="toggleDropdown('type')"
        >
          <span class="filter-label-inline">Type:</span>
          <input
            type="text"
            class="filter-select-inline"
            :class="{ 'is-placeholder': filters.type === 'all' }"
            :value="getDisplayText(typeOptions, filters.type)"
            readonly
            placeholder="Search.."
          />
          <img :src="downarrowIcon" alt="" class="select-arrow-icon" />
          <div v-if="openDd === 'type'" class="dropdown-panel" @click.stop>
            <div
              v-for="option in typeOptions"
              :key="option.value"
              class="dropdown-option"
              :class="{ active: filters.type === option.value }"
              @click="selectOption('type', option.value)"
            >
              <span>{{ option.label }}</span>
            </div>
          </div>
        </div>

        <div
          class="filter-box filter-box--select"
          :class="{ 'is-open': openDd === 'eligibleService' }"
          @click.stop="toggleDropdown('eligibleService')"
        >
          <span class="filter-label-inline">Eligible Service:</span>
          <input
            type="text"
            class="filter-select-inline"
            :class="{ 'is-placeholder': !filters.eligibleService }"
            :value="getDisplayText(eligibleServiceOptions, filters.eligibleService)"
            readonly
            placeholder="Search.."
          />
          <img :src="downarrowIcon" alt="" class="select-arrow-icon" />
          <div v-if="openDd === 'eligibleService'" class="dropdown-panel" @click.stop>
            <div
              v-for="option in eligibleServiceOptions"
              :key="option.value"
              class="dropdown-option"
              :class="{ active: filters.eligibleService === option.value }"
              @click="selectOption('eligibleService', option.value)"
            >
              <span>{{ option.label }}</span>
            </div>
          </div>
        </div>

        <div
          class="filter-box filter-box--select"
          :class="{ 'is-open': openDd === 'visibility' }"
          @click.stop="toggleDropdown('visibility')"
        >
          <span class="filter-label-inline">Visibility:</span>
          <input
            type="text"
            class="filter-select-inline"
            :class="{ 'is-placeholder': filters.visibility === 'all' }"
            :value="getDisplayText(visibilityOptions, filters.visibility)"
            readonly
            placeholder="Search.."
          />
          <img :src="downarrowIcon" alt="" class="select-arrow-icon" />
          <div v-if="openDd === 'visibility'" class="dropdown-panel" @click.stop>
            <div
              v-for="option in visibilityOptions"
              :key="option.value"
              class="dropdown-option"
              :class="{ active: filters.visibility === option.value }"
              @click="selectOption('visibility', option.value)"
            >
              <span>{{ option.label }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="filter-row filter-row-bottom">
        <div
          class="filter-box filter-box--select"
          :class="{ 'is-open': openDd === 'availability' }"
          @click.stop="toggleDropdown('availability')"
        >
          <span class="filter-label-inline">Availability:</span>
          <input
            type="text"
            class="filter-select-inline"
            :class="{ 'is-placeholder': filters.availability === 'all' }"
            :value="getDisplayText(availabilityOptions, filters.availability)"
            readonly
            placeholder="Search.."
          />
          <img :src="downarrowIcon" alt="" class="select-arrow-icon" />
          <div v-if="openDd === 'availability'" class="dropdown-panel" @click.stop>
            <div
              v-for="option in availabilityOptions"
              :key="option.value"
              class="dropdown-option"
              :class="{ active: filters.availability === option.value }"
              @click="selectOption('availability', option.value)"
            >
              <span>{{ option.label }}</span>
            </div>
          </div>
        </div>

        <div class="filter-actions-inline">
          <button class="btn-search" type="button" @click="handleSearch">Search</button>
          <button class="btn-clear" type="button" @click="handleClear">Clear</button>
        </div>
      </div>
    </div>

    <div class="action-bar">
      <span class="count-info" :class="{ 'count-info--pending': pageView === 'loading' }">
        {{ pageView === 'loading' ? 0 : totalCount }} Coupons
      </span>
      <button class="btn btn-new" type="button" @click="handleNew">+ NEW</button>
    </div>

    <div class="list-body" :aria-busy="pageView === 'loading'">
      <LoadingIndicator v-if="pageView === 'loading'" />
      <div v-else-if="totalCount === 0" class="orders-empty">
        <img :src="imgListBlank" class="orders-empty-img" alt="" />
        <p class="orders-empty-msg">No coupons found</p>
      </div>
      <div v-else class="list-container">
        <div v-for="coupon in couponList" :key="coupon.id" class="list-card">
          <div class="card-header">
            <span class="header-id">Coupon ID:</span>
            <button type="button" class="coupon-id-link" @click="handleViewCouponDetail(coupon.id)">
              {{ coupon.id }}
            </button>
          </div>
          <div class="card-body">
            <div class="column-main">
              <div class="coupon-info">
                <div class="coupon-name">{{ coupon.name }}</div>
                <div class="coupon-code">{{ coupon.code }}</div>
                <div class="coupon-meta">{{ coupon.validUntil }}</div>
                <div class="coupon-meta">{{ coupon.usage }}</div>
              </div>
            </div>
            <div class="column-value">
              <div class="column-label">{{ coupon.valueType }}</div>
              <div class="column-text">{{ coupon.value }}</div>
            </div>
            <div class="column-value">
              <div class="column-label">Spend Requirement</div>
              <div class="column-text">{{ coupon.spendRequirement }}</div>
            </div>
            <div class="column-value">
              <div class="column-label">Eligibility</div>
              <div class="column-text">{{ coupon.eligibility }}</div>
            </div>
            <div class="column-badges">
              <span
                class="status-label"
                :class="{
                  'status-label--visible': isCouponVisible(coupon),
                  'status-label--hidden': !isCouponVisible(coupon),
                }"
              >
                {{ coupon.visibility }}
              </span>
              <span
                class="status-label"
                :class="{
                  'status-label--active': isCouponActive(coupon),
                  'status-label--inactive': !isCouponActive(coupon),
                }"
              >
                {{ coupon.status }}
              </span>
            </div>
            <div class="column-actions">
              <button class="action-btn" type="button" @click.stop="handleSetVisibility(coupon)">
                <img :src="showIcon" alt="" class="action-icon" />
                Set Visibility
              </button>
              <button
                v-if="isCouponActive(coupon)"
                class="action-btn"
                type="button"
                @click.stop="handleToggleStatus(coupon)"
              >
                <img :src="scheduleIcon" alt="" class="action-icon" />
                Deactivate
              </button>
              <button v-else class="action-btn" type="button" @click.stop="handleToggleStatus(coupon)">
                <img :src="activateIcon" alt="" class="action-icon" />
                Reactivate
              </button>
              <button class="action-btn" type="button" @click.stop="handleCopy(coupon)">
                <img :src="duplicateIcon" alt="" class="action-icon" />
                Copy
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="pagination-wrapper">
      <PageComponent
        :current-page="currentPage"
        :total-pages="Math.max(1, totalPages)"
        @page-change="handlePageChange"
      />
    </div>

    <Teleport to="body">
      <ToastComponent :visible="toast.visible" :type="toast.type" :message="toast.message" />
    </Teleport>

    <CenterActivate
      v-if="showStatusModal && currentCoupon"
      :coupon-id="currentCoupon.id"
      :record-id="currentCoupon.recordId"
      :coupon-name="currentCoupon.name"
      :action="statusAction"
      @close="handleStatusClose"
      @saved="handleStatusSaved"
      @failed="handleStatusFailed"
    />

    <SetVisibility
      v-if="showVisibilityModal && currentCoupon"
      :coupon-id="currentCoupon.id"
      :record-id="currentCoupon.recordId"
      :initial-visibility="isCouponVisible(currentCoupon) ? 'visible' : 'hidden'"
      @close="handleVisibilityClose"
      @saved="handleVisibilitySaved"
      @failed="handleVisibilityFailed"
    />

    <CreateCoupon
      v-if="showCreateModal"
      :eligible-service-options="eligibleServiceOptions"
      @close="handleCreateClose"
      @saved="handleCreateSaved"
      @failed="handleCreateFailed"
    />

    <CopyPage
      v-if="showCopyModal && currentCopyCouponId"
      :key="currentCopyCouponId"
      :coupon-id="currentCopyCouponId"
      :eligible-service-options="eligibleServiceOptions"
      @close="handleCopyClose"
      @saved="handleCopySaved"
      @failed="handleCopyFailed"
    />

    <CouponDetail
      v-if="showDetailModal && currentCouponId"
      :coupon-id="currentCouponId"
      :record-id="currentDetailCoupon?.recordId ?? 0"
      :is-visible="currentDetailVisible"
      :is-active="currentDetailActive"
      @close="handleDetailClose"
      @set-visibility="handleDetailSetVisibility"
      @request-status="handleDetailRequestStatus"
      @updated="handleDetailUpdated"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, provide, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import PageComponent from '@/components/PageComponent.vue'
import LoadingIndicator from '@/components/LoadingIndicator.vue'
import ToastComponent from '@/components/ToastComponent.vue'
import CenterActivate from '@/views/CouponsPage/CenterActivate.vue'
import CouponDetail, {
  type CouponDetailRequestStatusPayload,
  type CouponDetailUpdatePayload,
} from '@/views/CouponsPage/CouponDetail.vue'
import CopyPage from '@/views/CouponsPage/CopyPage.vue'
import CreateCoupon from '@/views/CouponsPage/CreateCoupon.vue'
import SetVisibility from '@/views/CouponsPage/SetVisibility.vue'
import { couponCenterRoute, couponCopyRoute, couponDetailRoute } from '@/utils/libraryRoutes'
import { TOAST_DURATION } from '@/constants/toast'
import {
  buildAvailabilityOptions,
  buildCouponSearchTypes,
  buildCouponTypes,
  buildEligibleServices,
  buildVisibilityOptions,
  DEFAULT_COUPON_SEARCH_TYPES,
  postCouponCenterMeta,
  readCouponCenterListPrefill,
  type CouponMetaOption,
} from '@/api/CouponCenter'
import { isCouponApiSuccess } from '@/api/couponApi'
import {
  buildCouponCenterListParams,
  isCouponActive,
  isCouponVisible,
  mapCouponListItem,
  postCouponCenterList,
  type CouponItemView,
} from '@/api/CouponCenterList'
import downarrowIcon from '@/assets/downarrow.png'
import imgListBlank from '@/assets/list_blank@2x.png'
import duplicateIcon from '@/assets/Duplicate@2x.png'
import showIcon from '@/assets/show@2x.png'
import activateIcon from '@/assets/activate.png'
import scheduleIcon from '@/assets/Schedule.png'

type FilterKey = 'type' | 'eligibleService' | 'visibility' | 'availability'
type SearchField = 'coupon_code' | 'coupon_name' | 'code' | 'name'
type DropdownKey = FilterKey | 'searchField'
type FilterValue = 'all' | string
type PageView = 'loading' | 'content'

const LOADING_MIN_MS = 1500

interface SelectOption {
  label: string
  value: FilterValue
}

const couponList = ref<CouponItemView[]>([])
const totalPages = ref(1)
const totalCount = ref(0)
const pageView = ref<PageView>('loading')
let fetchGeneration = 0

const filters = reactive({
  searchField: 'coupon_code' as SearchField,
  searchQuery: '',
  type: 'all' as FilterValue,
  eligibleService: '' as FilterValue,
  visibility: 'all' as FilterValue,
  availability: 'all' as FilterValue,
})

const appliedFilters = reactive({
  searchField: 'coupon_code' as SearchField,
  searchQuery: '',
  type: 'all' as FilterValue,
  eligibleService: '' as FilterValue,
  visibility: 'all' as FilterValue,
  availability: 'all' as FilterValue,
})

const openDd = ref<DropdownKey | null>(null)

const searchFieldOptions = ref<CouponMetaOption[]>([...DEFAULT_COUPON_SEARCH_TYPES])

const typeOptions = ref<SelectOption[]>([])
const eligibleServiceOptions = ref<SelectOption[]>([])
const visibilityOptions = ref<SelectOption[]>([])
const availabilityOptions = ref<SelectOption[]>([])

const currentSearchFieldLabel = computed(() => {
  return searchFieldOptions.value.find((option) => option.value === filters.searchField)?.label ?? 'Coupon Code'
})

const currentPage = ref(1)
const pageSize = 10

const showStatusModal = ref(false)
const showVisibilityModal = ref(false)
const showCreateModal = ref(false)
const showDetailModal = ref(false)
const showCopyModal = ref(false)
const currentCouponId = ref('')
const currentCopyCouponId = ref('')

const currentDetailCoupon = computed(() =>
  couponList.value.find((item) => item.id === currentCouponId.value) ?? null,
)

const currentDetailVisible = computed(() =>
  currentDetailCoupon.value ? isCouponVisible(currentDetailCoupon.value) : true,
)

const currentDetailActive = computed(() =>
  currentDetailCoupon.value ? isCouponActive(currentDetailCoupon.value) : true,
)

const router = useRouter()
const route = useRoute()
const currentCoupon = ref<CouponItemView | null>(null)
const statusAction = ref<'deactivate' | 'reactivate'>('deactivate')

const toast = reactive({
  visible: false,
  type: 'success' as 'success' | 'warning' | 'error',
  message: '',
})

function showToast(type: 'success' | 'warning' | 'error', message: string): void {
  toast.type = type
  toast.message = message
  toast.visible = true
  setTimeout(() => {
    toast.visible = false
  }, TOAST_DURATION.brief)
}

provide<(type: 'success' | 'warning' | 'error', message: string) => void>('showToast', showToast)

function ensureSearchFieldValid(): void {
  if (!searchFieldOptions.value.some((option) => option.value === filters.searchField)) {
    filters.searchField = (searchFieldOptions.value[0]?.value as SearchField) ?? 'coupon_code'
    appliedFilters.searchField = filters.searchField
  }
}

async function loadMeta(): Promise<void> {
  try {
    const response = await postCouponCenterMeta({})
    if (isCouponApiSuccess(response.code) && response.data) {
      searchFieldOptions.value = buildCouponSearchTypes(response.data)
      typeOptions.value = buildCouponTypes(response.data)
      eligibleServiceOptions.value = buildEligibleServices(response.data)
      visibilityOptions.value = buildVisibilityOptions(response.data)
      availabilityOptions.value = buildAvailabilityOptions(response.data)
      ensureSearchFieldValid()
      return
    }
  } catch (error) {
    console.error('Failed to load coupon meta:', error)
  }

  searchFieldOptions.value = [...DEFAULT_COUPON_SEARCH_TYPES]
  typeOptions.value = buildCouponTypes(null)
  eligibleServiceOptions.value = buildEligibleServices(null)
  visibilityOptions.value = buildVisibilityOptions(null)
  availabilityOptions.value = buildAvailabilityOptions(null)
  ensureSearchFieldValid()
}

function buildListParams() {
  return buildCouponCenterListParams({
    page: currentPage.value,
    pageSize,
    searchField: appliedFilters.searchField,
    searchQuery: appliedFilters.searchQuery,
    type: appliedFilters.type,
    eligibleService: appliedFilters.eligibleService,
    visibility: appliedFilters.visibility,
    availability: appliedFilters.availability,
  })
}

function resolveRequestError(error: unknown, fallback: string): string {
  if (error && typeof error === 'object') {
    if ('response' in error) {
      const response = (error as {
        response?: {
          status?: number
          data?: {
            msg?: string
            message?: string
            datas?: { message?: string }
          }
        }
      }).response

      const datasMessage = response?.data?.datas?.message
      if (typeof datasMessage === 'string' && datasMessage.trim()) {
        if (datasMessage.includes('Base table or view not found')) {
          return 'Backend database table missing. Ask backend to run coupon DB migration.'
        }
        return datasMessage
      }

      if (response?.status === 404) {
        return 'Coupon API not found (404). Please confirm backend routes are deployed.'
      }
      if (typeof response?.data?.msg === 'string' && response.data.msg.trim()) {
        return response.data.msg
      }
      if (typeof response?.data?.message === 'string' && response.data.message.trim()) {
        return response.data.message
      }
    }
    if ('msg' in error && typeof (error as { msg: unknown }).msg === 'string') {
      return (error as { msg: string }).msg
    }
  }
  return fallback
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

async function fetchList(): Promise<void> {
  const generation = ++fetchGeneration
  pageView.value = 'loading'
  try {
    const [response] = await Promise.all([
      postCouponCenterList(buildListParams()),
      delay(LOADING_MIN_MS),
    ])
    if (generation !== fetchGeneration) return

    if (isCouponApiSuccess(response.code) && response.data) {
      couponList.value = (response.data.list || []).map(mapCouponListItem)
      totalPages.value = Math.max(1, response.data.pagination?.total_pages ?? 1)
      totalCount.value = response.data.pagination?.total ?? couponList.value.length
      return
    }

    couponList.value = []
    totalPages.value = 1
    totalCount.value = 0
    showToast('error', response.msg || 'Failed to fetch')
  } catch (error) {
    if (generation !== fetchGeneration) return
    console.error('Failed to load coupon list:', error)
    couponList.value = []
    totalPages.value = 1
    totalCount.value = 0
    showToast('error', resolveRequestError(error, 'Network request failed'))
  } finally {
    if (generation === fetchGeneration) {
      pageView.value = 'content'
    }
  }
}

function tryConsumeCouponCenterListPrefill(): void {
  const prefill = readCouponCenterListPrefill()
  if (!prefill) return
  const preferred = prefill.searchField.trim() || 'coupon_code'
  const field = (
    searchFieldOptions.value.some((o) => o.value === preferred) ? preferred : 'coupon_code'
  ) as SearchField
  filters.searchField = field
  filters.searchQuery = prefill.searchQuery
  appliedFilters.searchField = field
  appliedFilters.searchQuery = prefill.searchQuery
  currentPage.value = 1
}

async function beginDataLoad(): Promise<void> {
  await loadMeta()
  tryConsumeCouponCenterListPrefill()
  await fetchList()
}

function getDisplayText(options: SelectOption[], value: FilterValue): string {
  const matched = options.find((option) => option.value === value)
  if (matched) return matched.label
  return 'Search..'
}

function toggleDropdown(key: DropdownKey): void {
  openDd.value = openDd.value === key ? null : key
}

function closeAllDropdowns(): void {
  openDd.value = null
}

function selectSearchField(value: string): void {
  filters.searchField = value as SearchField
  openDd.value = null
}

function selectOption(key: FilterKey, value: FilterValue): void {
  filters[key] = value
  openDd.value = null
}

function handleSearch(): void {
  appliedFilters.searchField = filters.searchField
  appliedFilters.searchQuery = filters.searchQuery
  appliedFilters.type = filters.type
  appliedFilters.eligibleService = filters.eligibleService
  appliedFilters.visibility = filters.visibility
  appliedFilters.availability = filters.availability
  currentPage.value = 1
  closeAllDropdowns()
  void fetchList()
}

function handleClear(): void {
  filters.searchField = 'coupon_code'
  filters.searchQuery = ''
  filters.type = 'all'
  filters.eligibleService = ''
  filters.visibility = 'all'
  filters.availability = 'all'
  appliedFilters.searchField = 'coupon_code'
  appliedFilters.searchQuery = ''
  appliedFilters.type = 'all'
  appliedFilters.eligibleService = ''
  appliedFilters.visibility = 'all'
  appliedFilters.availability = 'all'
  currentPage.value = 1
  closeAllDropdowns()
  void fetchList()
}

function handlePageChange(page: number): void {
  currentPage.value = page
  void fetchList()
}

function handleNew(): void {
  showCreateModal.value = true
}

function handleCreateSaved(): void {
  showCreateModal.value = false
  void fetchList().then(() => {
    void nextTick(() => {
      showToast('success', 'Successfully saved')
    })
  })
}

function handleCreateFailed(): void {
  showToast('error', 'Failed to create coupon')
}

function handleCreateClose(): void {
  showCreateModal.value = false
}

function handleSetVisibility(coupon: CouponItemView): void {
  currentCoupon.value = coupon
  showVisibilityModal.value = true
}

function patchCouponVisibility(couponId: string, visibility: 'visible' | 'hidden'): void {
  const coupon = couponList.value.find((item) => item.id === couponId)
  if (!coupon) return

  coupon.visibility = visibility === 'visible' ? 'Visible' : 'Hidden'
  coupon.visibilityRaw = visibility
}

function handleVisibilitySaved(visibility?: 'visible' | 'hidden'): void {
  const couponId = currentCoupon.value?.id
  const detailWasOpen = showDetailModal.value
  showVisibilityModal.value = false
  currentCoupon.value = null

  if (couponId && visibility) {
    patchCouponVisibility(couponId, visibility)
  }

  // 从 Detail 进入设置成功后，一并关闭 Detail（与 Reactivate/Inactivate 一致）
  if (detailWasOpen) {
    handleDetailClose()
  }

  void fetchList().then(() => {
    void nextTick(() => {
      showToast('success', 'Successfully set')
    })
  })
}

function handleVisibilityFailed(): void {
  // 错误提示由 SetVisibility 弹窗内 toast 展示，避免重复
}

function handleVisibilityClose(): void {
  showVisibilityModal.value = false
  currentCoupon.value = null
}

function handleToggleStatus(coupon: CouponItemView): void {
  currentCoupon.value = coupon
  statusAction.value = isCouponActive(coupon) ? 'deactivate' : 'reactivate'
  showStatusModal.value = true
}

function patchCouponAvailability(couponId: string, active: boolean): void {
  const coupon = couponList.value.find((item) => item.id === couponId)
  if (!coupon) return

  coupon.status = active ? 'Active' : 'Inactive'
  coupon.availabilityRaw = active ? 'active' : 'inactive'
}

function handleStatusSaved(): void {
  const couponId = currentCoupon.value?.id
  const action = statusAction.value
  showStatusModal.value = false
  currentCoupon.value = null

  const message = action === 'deactivate' ? 'Successfully deactivated' : 'Successfully reactivated'

  if (couponId) {
    patchCouponAvailability(couponId, action === 'reactivate')
  }

  // 从详情启停时，确认成功后一并关掉详情
  if (showDetailModal.value) {
    handleDetailClose()
  }

  void fetchList().then(() => {
    void nextTick(() => {
      showToast('success', message)
    })
  })
}

function handleStatusFailed(): void {
  showStatusModal.value = false
  currentCoupon.value = null
}

function handleStatusClose(): void {
  showStatusModal.value = false
  currentCoupon.value = null
}

function handleViewCouponDetail(couponId: string): void {
  void router.push(couponDetailRoute(couponId))
}

function handleDetailSetVisibility(): void {
  const coupon = currentDetailCoupon.value
  if (!coupon) return
  handleSetVisibility(coupon)
}

function handleDetailRequestStatus(payload: CouponDetailRequestStatusPayload): void {
  const couponId = currentCouponId.value.trim()
  if (!couponId || !payload.recordId) {
    showToast('error', 'Coupon record ID is missing')
    return
  }

  const fromList = currentDetailCoupon.value
  currentCoupon.value = fromList
    ? {
        ...fromList,
        recordId: payload.recordId,
        name: payload.couponName || fromList.name,
      }
    : {
        id: couponId,
        recordId: payload.recordId,
        name: payload.couponName,
        code: '',
        validUntil: '',
        usage: '',
        valueType: '',
        value: '',
        spendRequirement: '',
        eligibility: '',
        visibility: '',
        status: '',
        availabilityRaw: '',
        visibilityRaw: '',
      }

  statusAction.value = payload.action
  showStatusModal.value = true
}

function handleDetailClose(): void {
  if (route.name === 'CouponDetail') {
    void router.push(couponCenterRoute())
    return
  }
  showDetailModal.value = false
  currentCouponId.value = ''
}

function handleDetailUpdated(payload: CouponDetailUpdatePayload): void {
  const couponId = currentCouponId.value
  const message =
    payload.availability === 'inactive' ? 'Successfully deactivated' : 'Successfully reactivated'

  if (couponId) {
    patchCouponAvailability(couponId, payload.availability === 'active')
  }

  handleDetailClose()

  void fetchList().then(() => {
    void nextTick(() => {
      showToast('success', message)
    })
  })
}

function syncCouponModalsFromRoute(): void {
  const routeName = route.name
  const couponId = String(route.params.couponId ?? '').trim()

  if (routeName === 'CouponDetail') {
    if (!couponId) {
      void router.replace(couponCenterRoute())
      return
    }
    currentCouponId.value = couponId
    showDetailModal.value = true
    showCopyModal.value = false
    currentCopyCouponId.value = ''
    return
  }

  if (routeName === 'CouponCopy') {
    if (!couponId) {
      void router.replace(couponCenterRoute())
      return
    }
    currentCopyCouponId.value = couponId
    showCopyModal.value = true
    showDetailModal.value = false
    currentCouponId.value = ''
    return
  }

  showDetailModal.value = false
  showCopyModal.value = false
  currentCouponId.value = ''
  currentCopyCouponId.value = ''
}

watch(
  () => [route.name, route.params.couponId] as const,
  () => {
    syncCouponModalsFromRoute()
  },
  { immediate: true },
)

function handleCopy(coupon: CouponItemView): void {
  void router.push(couponCopyRoute(coupon.id))
}

function handleCopyClose(): void {
  void router.push(couponCenterRoute())
}

function handleCopySaved(): void {
  void router.push(couponCenterRoute())
  void fetchList().then(() => {
    void nextTick(() => {
      showToast('success', 'Successfully saved')
    })
  })
}

function handleCopyFailed(): void {
  // CreateCoupon 已 toast 具体错误，这里不再重复弹「Failed to create coupon」
}

onMounted(() => {
  document.addEventListener('click', closeAllDropdowns)
  void beginDataLoad()
})

onUnmounted(() => {
  document.removeEventListener('click', closeAllDropdowns)
})
</script>

<style scoped>
.coupon-center-container {
  width: 100%;
  padding: 0;
  height: auto;
  overflow: visible;
}

.page-title {
  font-size: 18px;
  font-weight: 600;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  margin: 0 0 16px;
}

.filter-section {
  background-color: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
  padding: 16px;
  margin-bottom: 12px;
  box-sizing: border-box;
  overflow: visible;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.filter-row {
  display: grid;
  gap: 16px;
  align-items: center;
  width: 100%;
}

.filter-row-primary {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.filter-row-bottom {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.filter-box {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
  height: 32px;
  min-height: 32px;
  max-height: 32px;
  min-width: 0;
  padding: 0 12px;
  box-sizing: border-box;
  width: 100%;
  transition: border-color 0.15s ease;
}

.filter-box:focus-within,
.filter-box.is-open {
  border-color: #666666;
}

.field-search-item {
  gap: 0;
  padding: 0 12px;
}

.field-type-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  cursor: pointer;
  outline: none;
}

.field-type-label {
  font-size: 14px;
  font-weight: 600;
  color: #000000;
  font-family:
    'SF Pro',
    'SF Pro Display',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  white-space: nowrap;
}

.field-split {
  flex-shrink: 0;
  width: 1px;
  height: 16px;
  margin: 0 8px;
  background: #e0e0e0;
}

.downarrow-inline {
  flex-shrink: 0;
}

.search-field-panel {
  width: 100%;
}

.filter-actions-inline {
  display: flex;
  gap: 16px;
  justify-content: flex-end;
  grid-column: 4;
  justify-self: end;
}

.filter-label-inline {
  font-size: 14px;
  font-weight: 600;
  color: #000000;
  font-family:
    'SF Pro',
    'SF Pro Display',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  white-space: nowrap;
  flex-shrink: 0;
}

.filter-input-inline,
.filter-select-inline {
  flex: 1;
  border: none;
  outline: none;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #000000;
  background: transparent;
  min-width: 0;
}

.filter-input-inline::placeholder,
.filter-select-inline::placeholder {
  color: #cccccc;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.filter-select-inline {
  cursor: pointer;
}

.filter-select-inline.is-placeholder {
  color: #cccccc;
  font-weight: 400;
  -webkit-text-fill-color: #cccccc;
}

.select-arrow-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  pointer-events: none;
  transition: transform 0.2s ease;
  filter: brightness(0) saturate(100%) invert(42%) sepia(0%) saturate(0%) hue-rotate(0deg)
    brightness(100%) contrast(100%);
}

.filter-box.is-open .select-arrow-icon {
  transform: rotate(180deg);
}

.dropdown-panel {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  margin-top: 8px;
  padding: 4px 0;
  z-index: 1000;
  max-height: 300px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.dropdown-option {
  padding: 8px 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  color: #000000;
  font-size: 14px;
  line-height: 16px;
  font-weight: 400;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
}

.dropdown-option:hover {
  background: #f5f5f5;
}

.dropdown-panel .dropdown-option.active span {
  font-weight: 600 !important;
}

.btn-search {
  width: 80px;
  height: 32px;
  padding: 0;
  background-color: #000000;
  color: #fff;
  border: none;
  font-family:
    'SF Pro Display',
    -apple-system,
    BlinkMacSystemFont,
    'SF Pro',
    system-ui,
    sans-serif;
  border-radius: 16px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-search:hover {
  background-color: #484848;
}

.btn-clear {
  width: 80px;
  height: 32px;
  padding: 0;
  background-color: #b0b0b0;
  color: #ffffff;
  border: none;
  border-radius: 16px;
  font-family:
    'SF Pro Display',
    -apple-system,
    BlinkMacSystemFont,
    'SF Pro',
    system-ui,
    sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-clear:hover {
  background-color: #cccccc;
}

.action-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 32px;
  min-height: 32px;
  margin-bottom: 12px;
  box-sizing: border-box;
}

.count-info {
  font-size: 16px;
  font-weight: 600;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  min-height: 24px;
}

.count-info--pending {
  visibility: hidden;
}

.btn-new {
  box-sizing: border-box;
  width: 80px;
  height: 32px;
  padding: 0;
  background: #00aeef;
  color: #ffffff;
  border: 1px solid #ffffff;
  border-radius: 16px;
  font-size: 14px;
  font-weight: 700;
  line-height: 14px;
  letter-spacing: 0;
  text-align: center;
  white-space: nowrap;
  font-family:
    -apple-system,
    BlinkMacSystemFont,
    'SF Pro Text',
    'SF Pro',
    system-ui,
    sans-serif;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: pointer;
}

.btn-new:hover {
  background: #2a5d8f;
}

.list-body {
  position: relative;
}

.orders-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 16px 32px;
  box-sizing: border-box;
}

.orders-empty-img {
  width: 200px;
  height: 200px;
  object-fit: contain;
  display: block;
  flex-shrink: 0;
}

.orders-empty-msg {
  margin: 12px 0 0;
  font-size: 24px;
  font-weight: 500;
  color: #b0b0b0;
}

.list-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.list-card {
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
  overflow: hidden;
}

.card-header {
  height: 32px;
  padding: 0 16px;
  background: #f7f7f7;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
  font-size: 14px;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
}

.header-id {
  color: #999999;
}

.coupon-id-link {
  margin-left: 4px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  font: inherit;
  color: #000000;
  line-height: 20px;
  text-decoration: underline;
  text-decoration-color: #000000;
  text-underline-offset: 2px;
}

.card-body {
  display: grid;
  grid-template-columns:
    minmax(0, 2.2fr)
    minmax(0, 1fr)
    minmax(0, 1fr)
    minmax(0, 1fr)
    minmax(0, 0.75fr)
    minmax(0, 1fr);
  min-height: 136px;
}

.card-body > div {
  padding: 16px;
  border-right: 1px solid #e0e0e0;
  display: flex;
  min-width: 0;
  box-sizing: border-box;
}

.card-body > div:last-child {
  border-right: none;
}

.card-body > .column-main {
  align-items: flex-start;
  justify-content: flex-start;
}

.column-main {
  align-items: flex-start;
  justify-content: flex-start;
}

.coupon-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  text-align: left;
}

.coupon-name,
.coupon-code,
.coupon-meta {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #000000;
  text-align: left;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
}

.coupon-code {
  letter-spacing: 0.04em;
}

.card-body > .column-value {
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-align: center;
}

.column-value {
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-align: center;
}

.column-label {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #000000;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
}

.column-text {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #000000;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
}

.card-body > .column-badges {
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-align: center;
}

.column-badges {
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-align: center;
}

.status-label {
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  background: none;
  text-align: center;
  white-space: nowrap;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
}

.status-label--visible {
  color: #00aeef;
}

.status-label--hidden {
  color: #cccccc;
}

.status-label--active {
  color: #98d5be;
}

.status-label--inactive {
  color: #e54848;
}

.card-body > .column-actions {
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
  justify-content: center;
}

.column-actions {
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
  justify-content: center;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0;
  border: none;
  background: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  cursor: pointer;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  transition: all 0.2s;
  white-space: nowrap;
  flex-shrink: 0;
}

.action-icon {
  width: 16px;
  height: 16px;
  object-fit: contain;
}

.pagination-wrapper {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
}

@media (max-width: 1200px) {
  .filter-row-primary,
  .filter-row-bottom {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .filter-row-bottom .filter-actions-inline {
    grid-column: 2;
  }

  .card-body {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  }

  .card-body > .column-main {
    grid-column: 1 / -1;
  }

  .card-body > .column-badges {
    align-items: center;
    justify-content: center;
  }

  .card-body > div {
    border-right: none;
    border-bottom: 1px solid #e0e0e0;
  }
}

@media (max-width: 720px) {
  .filter-row-primary,
  .filter-row-bottom {
    grid-template-columns: 1fr;
  }

  .filter-row-bottom .filter-actions-inline {
    grid-column: auto;
    justify-self: stretch;
    justify-content: flex-end;
  }

  .card-body {
    grid-template-columns: 1fr;
  }
}
</style>
